import { useEffect, useId, useRef, useState, type CSSProperties } from "react";
import { adminSearchRecipeIngredients, type RecipeIngredientOption } from "../../lib/api";

interface Props {
  label: string;
  selected: RecipeIngredientOption | null;
  onSelect: (option: RecipeIngredientOption) => void;
  /** `name` is the unmatched search text when the user asks to enter their own, else "". */
  onCreate: (name: string) => void;
}

/** Selection lives in the recipe row, independently of transient search results. */
export default function RecipeIngredientPicker({ label, selected, onSelect, onCreate }: Props) {
  const id = useId();
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<RecipeIngredientOption[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const [offset, setOffset] = useState(0);
  const generation = useRef(0);
  const term = query.trim();

  function clearSearch(value: string) {
    // Invalidate immediately, including the interval before the next effect runs.
    generation.current++;
    setQuery(value);
    setItems([]);
    setHasMore(false);
    setError(null);
    setLoading(false);
    setOffset(0);
  }

  useEffect(() => {
    const request = ++generation.current;
    const term = query.trim();
    if (term.length < 2) return;
    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      void adminSearchRecipeIngredients(term, offset).then((result) => {
        if (generation.current !== request) return;
        setItems((previous) => offset === 0 ? result.items : [...previous, ...result.items]);
        setHasMore(result.has_more);
      }).catch((err: unknown) => {
        if (generation.current === request) {
          setError(err instanceof Error ? err.message : "Could not search ingredients");
        }
      }).finally(() => {
        if (generation.current === request) setLoading(false);
      });
    }, 300);
    return () => { window.clearTimeout(timer); generation.current++; };
  }, [query, offset, retry]);

  return (
    <div style={{ display: "grid", gap: 8, minWidth: 0 }}>
      <label htmlFor={id}>{label}</label>
      <input id={id} type="search" value={query} autoComplete="off"
        placeholder="Search bottles and ingredients…" aria-describedby={`${id}-status`}
        onChange={(event) => clearSearch(event.target.value)}
        onKeyDown={(event) => { if (event.key === "Enter") event.preventDefault(); }}
        style={{ ...control, width: "100%", boxSizing: "border-box" }} />
      <div id={`${id}-status`} role="status" style={{ color: "var(--muted)", fontSize: 13 }}>
        {loading ? "Searching…" : query.trim().length < 2 ? "Type at least 2 characters to search." : !error && items.length === 0 ? "No matching bottles or ingredients." : `${items.length} results`}
      </div>
      {error && <div role="alert">
        <p>{error}</p>
        <button type="button" style={control} onClick={() => setRetry((n) => n + 1)}>Retry search</button>
      </div>}
      {items.length > 0 && <ul aria-label="Ingredient search results" style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
        {items.map((item) => <li key={`${item.source}:${item.id}`} style={{ minWidth: 0 }}>
          <button type="button" disabled={!item.available}
            aria-pressed={selected?.id === item.id && selected.source === item.source}
            onClick={() => { onSelect(item); clearSearch(""); }}
            style={{ ...control, width: "100%", textAlign: "left", overflowWrap: "anywhere", whiteSpace: "normal" }}>
            {item.name}<span style={{ display: "block", fontSize: 12, color: "var(--muted)" }}>
              {item.category}{item.is_untracked ? " · Basic/Untracked" : ""}{!item.available ? " · Unavailable" : ""}
            </span>
          </button>
        </li>)}
      </ul>}
      {hasMore && !error && <button type="button" style={control} disabled={loading}
        onClick={() => setOffset((current) => current + 30)}>Load more results</button>}
      {term.length >= 2 && !loading && !items.some((item) => item.name.trim().toLowerCase() === term.toLowerCase()) &&
        <button type="button" style={{ ...control, overflowWrap: "anywhere", whiteSpace: "normal", textAlign: "left" }}
          onClick={() => { onCreate(term); clearSearch(""); }}>
          + Use “{term}” — enter my own
        </button>}
      <button type="button" style={control} onClick={() => onCreate("")}>+ Add new ingredient / bottle</button>
    </div>
  );
}

const control: CSSProperties = {
  minHeight: 48, minWidth: 0, padding: "10px 12px", borderRadius: 8,
  border: "1px solid var(--border, #454545)", background: "var(--panel, #181818)",
  color: "var(--text, #fff)", font: "inherit",
};
